import React from 'react';
import { Typography, Tooltip, Select, Spin } from 'antd';
import { QuestionCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import LabelExtractionManager from '@/features/datasets/components/LabelExtractionManager';
import RejectionLabelNotice from '@/features/datasets/components/RejectionLabelNotice';
import HasLabelsSwitch from '@/features/datasets/components/datasetUpload/HasLabelsSwitch';
import NoLabelsModeChoice from '@/features/datasets/components/datasetUpload/NoLabelsModeChoice';
import AiGeneratedLabelsBlock from '@/features/datasets/components/datasetUpload/AiGeneratedLabelsBlock';
import EmptyResponsesChoice from '@/features/datasets/components/datasetUpload/EmptyResponsesChoice';
import {
    NO_LABELS_MODE_DEFAULT,
    NO_LABELS_MODE_GENERATE_WITH_AI,
    NO_LABELS_MODE_UNLABELED,
} from '@/features/datasets/utils/hasLabelsHandling';
import { TaskType } from '@/shared/domain/taskTypes';
const { Text } = Typography;

const InlineColumnSelector = ({ label, value, onChange, options = [], placeholder = "Select column", loading = false, error, title, tooltipText }) => {
    const headingText = title || `Select ${label} column`;
    const tooltip = tooltipText || (label === "labels" ? "Select the column that contains the labels for the examples" : "Select the column that contains the examples");
    const renderOption = (opt) => {
        if (typeof opt === 'string') return <Select.Option key={opt} value={opt}>{opt}</Select.Option>;
        const v = opt?.value ?? ''; const l = opt?.label ?? String(v);
        return <Select.Option key={v} value={v}>{l}</Select.Option>;
    };
    return (
        <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                <Text style={{ fontWeight: 400, color: '#222', fontSize: '14px', margin: 0 }}>{headingText}</Text>
                <Tooltip title={tooltip}>
                    <QuestionCircleOutlined style={{ color: '#878787', fontSize: '12px', cursor: 'help' }} />
                </Tooltip>
            </div>
            <Select value={value} onChange={onChange} placeholder={loading ? 'Loading...' : placeholder} loading={loading} disabled={loading} notFoundContent={loading ? <Spin size="small" /> : null} suffixIcon={loading ? <LoadingOutlined style={{ color: '#878787' }} spin /> : undefined} style={{ width: '100%' }} allowClear>
                {options.map(renderOption)}
            </Select>
            {error ? <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 6 }}>{error}</div> : null}
        </div>
    );
};

/**
 * Per-objective form for a Classification output (single- or multi-label).
 *
 * Pure presentation: state is owned by `useUploadModalState` in the modal
 * shell; this form just reads from `entry` and dispatches via `onPatch`.
 *
 * When `entry.hasLabels === false`, the column/extracted-labels UI is hidden
 * and the user chooses unlabeled vs generate-with-AI. `aiLabels` is a
 * per-tab field on the same entry (only for generate-with-AI).
 *
 * @param {Object} props
 * @param {Object} props.entry - per-output state slice (see useUploadModalState).
 * @param {(patch: Object) => void} props.onPatch - patch the entry for this objective.
 * @param {Array} props.columns - file columns.
 * @param {boolean} props.columnsLoading
 * @param {boolean} props.isMultilabel
 * @param {boolean} [props.labelLimitExceeded]
 * @param {Array} [props.labelLimitSamples]
 * @param {boolean} [props.labelsLoading]
 * @param {Array} [props.extractedLabels]
 * @param {Object} [props.labelExamples]
 * @param {Array} [props.availableLabels]
 * @param {string} [props.labelError]
 * @param {string} [props.clientId] - For the AI label suggestion endpoint.
 * @param {string} [props.usecaseId]
 * @param {() => Array<string>} [props.getExamples] - Callback returning examples for the AI block.
 */
const ClassificationOutputForm = ({
    entry,
    onPatch,
    columns,
    columnsLoading,
    isMultilabel,
    labelLimitExceeded,
    labelLimitSamples = [],
    labelsLoading,
    extractedLabels = [],
    labelExamples = {},
    availableLabels = [],
    labelError,
    onDeleteLabel,
    onReplaceLabel,
    onAddNewLabel,
    clientId,
    usecaseId,
    getExamples,
    confidenceRejectionEnabled = false,
}) => {
    const hasLabels = entry?.hasLabels !== false;
    const noLabelsMode = entry?.noLabelsMode || NO_LABELS_MODE_DEFAULT;
    const showAiBlock =
        !hasLabels && noLabelsMode === NO_LABELS_MODE_GENERATE_WITH_AI;

    return (
        <div>
            <HasLabelsSwitch
                taskType={TaskType.classification}
                checked={hasLabels}
                onChange={(checked) => {
                    if (checked) {
                        // Re-enable column flow; clear AI suggestions so we
                        // never submit two sources at once.
                        onPatch({
                            hasLabels: true,
                            noLabelsMode: NO_LABELS_MODE_DEFAULT,
                            aiLabels: [],
                        });
                    } else {
                        // Hide column/extracted-label UI and clear any picked
                        // column to avoid stale submissions.
                        onPatch({
                            hasLabels: false,
                            noLabelsMode: NO_LABELS_MODE_DEFAULT,
                            labelColumn: null,
                            extractedLabels: [],
                            labelMappings: {},
                            labelExamples: {},
                        });
                    }
                }}
            />

            {!hasLabels ? (
                <>
                    <NoLabelsModeChoice
                        taskType={TaskType.classification}
                        value={noLabelsMode}
                        onChange={(mode) => {
                            onPatch({
                                noLabelsMode: mode,
                                ...(mode === NO_LABELS_MODE_UNLABELED
                                    ? { aiLabels: [] }
                                    : {}),
                            });
                        }}
                    />
                    {showAiBlock ? (
                        <AiGeneratedLabelsBlock
                            clientId={clientId}
                            usecaseId={usecaseId}
                            taskType={TaskType.classification}
                            aiLabels={entry?.aiLabels || []}
                            onChange={(rows) => onPatch({ aiLabels: rows })}
                            availableLabels={availableLabels}
                            getExamples={getExamples}
                            showRejectionColumn={!isMultilabel && confidenceRejectionEnabled}
                            rejectionLabelName={entry?.rejectionLabelName || null}
                            onSetRejectionLabel={(name) => onPatch({ rejectionLabelName: name })}
                        />
                    ) : null}
                </>
            ) : (
                <>
                    <InlineColumnSelector
                        label="labels"
                        title="Select labels field"
                        tooltipText="Select the column that contains the labels for the examples"
                        value={entry.labelColumn}
                        onChange={(v) =>
                            onPatch(
                                v
                                    ? {
                                          labelColumn: v,
                                          detectedLabelColumn: v,
                                          ...(isMultilabel && v !== entry.labelColumn
                                              ? { delimiter: null }
                                              : {}),
                                      }
                                    : {
                                          labelColumn: v,
                                          ...(isMultilabel ? { delimiter: null } : {}),
                                      }
                            )
                        }
                        options={columns}
                        placeholder="Select labels column"
                        loading={columnsLoading}
                        error={labelError}
                    />
                    {isMultilabel && entry.labelColumn && (
                        <InlineColumnSelector
                            label="labels"
                            title="Select labels separator (delimiter)"
                            tooltipText="Select the separator used to separate labels in the labels column"
                            value={entry.delimiter}
                            onChange={(v) => onPatch({ delimiter: v })}
                            options={[
                                { value: ';', label: 'Semicolon ;' },
                                { value: ',', label: 'Comma ,' },
                                { value: ' ', label: 'Space ' },
                                { value: '|', label: 'Pipe |' },
                                { value: '\t', label: 'Tab \\t' },
                                { value: 'list', label: 'List []' },
                            ]}
                            placeholder="Delimiter"
                        />
                    )}
                    {entry.labelColumn && !(isMultilabel && !entry.delimiter) && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    marginBottom: 8,
                                }}
                            >
                                <Text style={{ fontWeight: 600, color: '#222', fontSize: 14, margin: 0 }}>
                                    Labels
                                </Text>
                                <Tooltip title="Review and normalize extracted labels">
                                    <QuestionCircleOutlined style={{ color: '#878787', fontSize: 14 }} />
                                </Tooltip>
                            </div>
                            <LabelExtractionManager
                                labelColumn={entry.labelColumn}
                                extractedLabels={extractedLabels}
                                loading={labelsLoading}
                                labelMappings={entry.labelMappings}
                                onDeleteLabel={onDeleteLabel}
                                onReplaceLabel={onReplaceLabel}
                                onAddNewLabel={onAddNewLabel}
                                labelType="Label"
                                taskType={TaskType.classification}
                                isMultilabel={isMultilabel}
                                labelExamples={labelExamples}
                                availableLabels={availableLabels}
                                showRejectionColumn={!isMultilabel && confidenceRejectionEnabled}
                                rejectionLabelName={entry.rejectionLabelName || null}
                                onSetRejectionLabel={(name) => onPatch({ rejectionLabelName: name })}
                            />
                            {!isMultilabel && confidenceRejectionEnabled && (
                                <RejectionLabelNotice
                                    extractedLabels={extractedLabels}
                                    labelMappings={entry.labelMappings}
                                    rejectionLabelName={entry.rejectionLabelName || null}
                                    onSetRejectionLabel={(name) => onPatch({ rejectionLabelName: name })}
                                />
                            )}
                            {labelLimitExceeded && labelLimitSamples.length > 0 && (
                                <Text style={{ color: '#8c8c8c', fontSize: 12 }}>
                                    Sample labels shown - full extraction continues server-side.
                                </Text>
                            )}
                            <EmptyResponsesChoice
                                isMultilabel={isMultilabel}
                                taskType={TaskType.classification}
                                value={entry.emptyMultilabelHandling || (isMultilabel ? 'none_selected' : 'unlabeled')}
                                onChange={(v) => onPatch({ emptyMultilabelHandling: v })}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ClassificationOutputForm;
